/**
 * Section classifier — anchor-based detection tuned for dance/house tracks.
 *
 * The previous approach used min-max percentile thresholds, which collapses
 * for tracks with one outlier peak: most middle segments fall below 35%
 * percentile and get labeled "breakdown", producing a 5-minute "breakdown"
 * for a typical house track. The new approach is anchor-based:
 *
 *   1. Bookend with mix-in / mix-out if the very ends are low energy.
 *   2. Detect drops: pick the top energy segments in [20%, 85%] position
 *      with mutual spacing — guarantees at least one drop on any track that
 *      has a real peak.
 *   3. Mark the 1–2 segments before each drop as "build" if energy is rising.
 *   4. Mark sustained low-energy regions (> ~16s, energy < median * 0.85)
 *      between drops as "breakdown".
 *   5. Intro = pre-first-build; Outro = post-last-drop tail.
 *   6. Remaining segments → chorus (above median) / verse (at-or-below median),
 *      with proximity to a drop biasing toward chorus.
 *   7. Coalesce, drop tiny sections, cap counts, number repeats.
 */

export type SectionType =
  | 'mix-in'
  | 'intro'
  | 'breakdown'
  | 'main-drop'
  | 'bridge'
  | 'outro'
  | 'mix-out'
  | 'verse'
  | 'chorus'
  | 'build';

export interface Segment {
  segmentNumber: number;
  startTime: number;
  endTime: number;
  energy?: number;
  chordProgression?: string[];
  key?: string;
  instruments?: Array<{ type: string; confidence: number }>;
  mixing?: {
    volumeDb?: number;
    panPosition?: number;
    frequencySpectrum?: Record<string, number>;
    dynamicRange?: number;
    compressionRatio?: number;
  } | null;
}

export interface Section {
  type: SectionType;
  /** Display label including numbering when there are multiple of the same type, e.g. "Verse 2". */
  label: string;
  /** 1-indexed ordinal among sections of the same type (only set when count > 1). */
  index?: number;
  startTime: number;
  endTime: number;
  segments: Segment[];
  confidence: number;
  avgEnergy: number;
  dominantKey?: string;
  chordProgression?: string[];
  instruments?: Array<{ type: string; confidence: number }>;
  timestamp: string;
  /**
   * Set on user-inserted sections (split-by-playhead or fill-a-gap). The
   * classifier never emits this; ResultsPage attaches it after merging
   * inserts into the rendered list. Routes editor commits to the insert
   * paths instead of update-by-index.
   */
  insertId?: string;
  /**
   * Original classifier-emission index, propagated through the merged
   * sorted list so the editor can update the correct override slot even
   * after the merge order shifts (because of an earlier boundary edit or
   * because user-inserted sections changed positions in the list).
   * Undefined for inserts.
   */
  classifierIndex?: number;
}

const MAX_COUNTS: Record<SectionType, number> = {
  'mix-in':    1,
  'intro':     1,
  'breakdown': 2,
  'main-drop': 3, // electronic tracks frequently have 3 drops
  'bridge':    1,
  'outro':     1,
  'mix-out':   1,
  'verse':     2,
  'chorus':    3,
  'build':     2,
};

/** Genre families that pick "Drop" terminology over "Chorus". */
const ELECTRONIC_FAMILIES = new Set(['electronic']);

const MIN_SECTION_FRACTION = 0.04;
const MIN_SECTION_SECONDS = 10;

const MAX_DROPS = 2;
const MIN_DROP_GAP_SECONDS = 30;
const DROP_POSITION_RANGE: [number, number] = [0.18, 0.88];
const MIN_BREAKDOWN_SECONDS = 16;

/**
 * Optional high-resolution amplitude series used for drop / breakdown
 * detection. When `lowSeries` (low-band peaks ~ kick channel) is provided it
 * is preferred; else `peakSeries` (overall peaks) is used. When neither is
 * provided the classifier falls back to per-segment energy.
 *
 * Series values are normalized 0..1, evenly spaced over `totalDuration`.
 */
export interface ClassifierOpts {
  peakSeries?: number[];
  lowSeries?: number[];
  /** Total track duration in seconds. Defaults to last segment's endTime. */
  totalDuration?: number;
  /**
   * Genre family from the worker's classifier. When `electronic`, peak-energy
   * sections are labelled "Drop" (numbered) instead of "Chorus" — house /
   * techno / EDM tracks have drops, not choruses.
   */
  genreFamily?: 'electronic' | 'pop' | 'hiphop' | 'rock' | 'jazz' | 'ambient' | 'unknown';
}

export function classifySections(
  segments: Segment[],
  opts: ClassifierOpts = {},
): Section[] {
  if (segments.length === 0) return [];

  const totalDuration =
    opts.totalDuration ?? segments[segments.length - 1]?.endTime ?? 0;

  // Prefer low-band over overall peaks — for dance music it's a much cleaner
  // drop signal (kick drops out during breakdowns, returns at the drop).
  const series = opts.lowSeries ?? opts.peakSeries;
  const smoothedSeries = series ? smooth(series, 5) : null;

  // When a high-res series is available, derive each segment's energy from
  // the low-band waveform rather than the worker's per-8-bar segment.energy
  // (which over-weights melodic content during breakdowns and labels them as
  // chorus). With the low-band signal, breakdowns score correctly low.
  const energies = smoothedSeries
    ? perSegmentEnergyFromSeries(segments, smoothedSeries, totalDuration)
    : segments.map((s) => s.energy || 0);
  const median = percentile(energies, 0.5);
  const p75 = percentile(energies, 0.75);
  const p25 = percentile(energies, 0.25);

  // Shallow-clone each segment with its low-band-derived energy so that the
  // downstream coalesce / finalize / display code reads the *new* values
  // instead of the worker's raw mean-energy (which is what was bottoming out
  // the Energy Profile bar at 0 %). The classifier never mutates the
  // caller's segment objects.
  const segs = smoothedSeries
    ? segments.map((s, i) => ({ ...s, energy: energies[i] }))
    : segments;

  const types: (SectionType | null)[] = new Array(segments.length).fill(null);

  // 1. Mix-in / Mix-out bookends
  if (segments.length > 0) {
    const first = segments[0];
    if ((first.energy || 0) < median * 0.7 && first.startTime / totalDuration < 0.05) {
      types[0] = 'mix-in';
    }
    const lastIdx = segments.length - 1;
    const last = segments[lastIdx];
    if ((last.energy || 0) < median * 0.7 && last.startTime / totalDuration > 0.95) {
      types[lastIdx] = 'mix-out';
    }
  }

  // 2. Drops — when a high-resolution series is available, use it; else fall
  //    back to per-segment energy.
  const dropIdxs = smoothedSeries
    ? pickDropsFromSeries(smoothedSeries, totalDuration, segments)
    : pickDrops(segments, energies, totalDuration, p75);
  dropIdxs.forEach((i) => (types[i] = 'main-drop'));

  // 3. Builds — mark the 1–2 segments before each drop if energy is rising.
  for (const dropIdx of dropIdxs) {
    for (let look = 1; look <= 2; look++) {
      const i = dropIdx - look;
      if (i < 0) break;
      if (types[i] !== null) break;
      const e = energies[i];
      if (e < energies[dropIdx] && e >= median) {
        types[i] = 'build';
      } else {
        break;
      }
    }
  }

  // 4. Breakdowns — sustained low-energy runs between drops. When a
  //    high-res series is available use it; else fall back to segment energy.
  if (dropIdxs.length > 0) {
    if (smoothedSeries) {
      markBreakdownsFromSeries(types, segments, smoothedSeries, totalDuration, dropIdxs);
    } else {
      markBreakdowns(types, segments, energies, dropIdxs, median);
    }
  }

  // 5. Intro / Outro — any unmarked segment before the first STRUCTURAL
  //    anchor is intro; any unmarked segment after the last is outro. We
  //    explicitly skip mix-in/mix-out bookends here — otherwise mix-in at
  //    index 0 makes firstAnchorIdx=0 and `i < 0` never matches, leaving
  //    the rest of the intro to fall through to the chorus / verse fill.
  const isStructural = (t: SectionType | null): boolean =>
    !!t && t !== 'mix-in' && t !== 'mix-out';
  const firstAnchorIdx = types.findIndex(isStructural);
  let lastAnchorIdx = -1;
  for (let i = types.length - 1; i >= 0; i--) {
    if (isStructural(types[i])) { lastAnchorIdx = i; break; }
  }
  for (let i = 0; i < segments.length; i++) {
    if (types[i] !== null) continue;
    if (firstAnchorIdx !== -1 && i < firstAnchorIdx) {
      types[i] = 'intro';
    } else if (lastAnchorIdx !== -1 && i > lastAnchorIdx) {
      types[i] = 'outro';
    }
  }

  // 6. Verses / choruses — fill the rest based on energy vs. median and
  //    proximity to a drop. We keep `chorus` as the *structural* type so the
  //    coalesce step preserves diversity (Intro / Chorus / Breakdown / …).
  //    For electronic genres we relabel `chorus` → "Drop N" at display time
  //    in numberRepeats() — type stays distinct so segments don't all merge.
  for (let i = 0; i < segments.length; i++) {
    if (types[i] !== null) continue;
    const e = energies[i];
    const nearDrop = dropIdxs.some((d) => Math.abs(i - d) <= 2);
    if (e >= p75 || (e >= median && nearDrop)) {
      types[i] = 'chorus';
    } else if (e <= p25) {
      types[i] = segments[i].startTime / totalDuration < 0.5 ? 'verse' : 'breakdown';
    } else {
      types[i] = 'verse';
    }
  }

  // 7. Coalesce + post-process. Use `segs` (clones with low-band energy)
  //    so the section.avgEnergy that the UI reads reflects the same signal
  //    that drove the classification.
  let sections = coalesce(segs, types as SectionType[]);
  sections = dropTiny(sections, totalDuration);
  sections = enforceCaps(sections);
  sections = coalesceSections(sections);
  numberRepeats(sections, opts.genreFamily);
  sections.forEach(finalizeSection);
  normalizeEnergies(sections);
  return sections;
}

/**
 * The worker returns Essentia's raw mean-energy per segment, which is
 * commonly in the 0.001–0.1 range — multiplying by 100 for display reads as
 * "0%". Rescale every section's avgEnergy so the loudest section sits near
 * 100 % and the quietest near 0–10 %, preserving relative ordering.
 */
function normalizeEnergies(sections: Section[]): void {
  if (sections.length === 0) return;
  const values = sections.map((s) => s.avgEnergy ?? 0);
  const max = Math.max(...values);
  // If the values are already in a sensible 0–1 range we leave them alone.
  if (max <= 0 || max >= 0.6) return;
  const scale = 1 / max;
  sections.forEach((s) => {
    s.avgEnergy = Math.min(1, (s.avgEnergy ?? 0) * scale);
  });
}

// ───────────────────────── helpers: distribution ─────────────────────────

function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor(sorted.length * p)));
  return sorted[idx];
}


// ───────────────────────── drop detection ─────────────────────────

/**
 * High-resolution drop detection from an amplitude series. Returns the
 * segment indices that contain the detected peak times.
 *
 * Algorithm: smooth → find local maxima inside the legal position range with
 * a minimum prominence (50% of the global series max) and minimum spacing,
 * then take the top-N by amplitude.
 */
function pickDropsFromSeries(
  series: number[],
  totalDuration: number,
  segments: Segment[],
): number[] {
  if (series.length === 0 || totalDuration <= 0) return [];

  const seriesMax = Math.max(...series);
  if (seriesMax === 0) return [];

  const prominenceFloor = seriesMax * 0.5; // candidate must be ≥ 50% of peak
  const minBinGap = Math.max(
    1,
    Math.floor((MIN_DROP_GAP_SECONDS / totalDuration) * series.length),
  );
  const lo = Math.floor(DROP_POSITION_RANGE[0] * series.length);
  const hi = Math.floor(DROP_POSITION_RANGE[1] * series.length);

  // Collect local maxima
  const maxima: Array<{ idx: number; v: number }> = [];
  for (let i = Math.max(1, lo); i < Math.min(series.length - 1, hi); i++) {
    if (
      series[i] >= prominenceFloor &&
      series[i] >= series[i - 1] &&
      series[i] >= series[i + 1]
    ) {
      maxima.push({ idx: i, v: series[i] });
    }
  }
  // If nothing meets the floor, relax to anything ≥ 35% of peak
  if (maxima.length === 0) {
    const relaxed = seriesMax * 0.35;
    for (let i = Math.max(1, lo); i < Math.min(series.length - 1, hi); i++) {
      if (
        series[i] >= relaxed &&
        series[i] >= series[i - 1] &&
        series[i] >= series[i + 1]
      ) {
        maxima.push({ idx: i, v: series[i] });
      }
    }
  }
  if (maxima.length === 0) return [];

  // Greedy: top by amplitude, enforce min spacing
  maxima.sort((a, b) => b.v - a.v);
  const pickedBins: number[] = [];
  for (const m of maxima) {
    if (pickedBins.every((p) => Math.abs(p - m.idx) >= minBinGap)) {
      pickedBins.push(m.idx);
    }
    if (pickedBins.length >= MAX_DROPS) break;
  }

  // Map bin indices → segment indices
  const segmentIdxs = pickedBins
    .map((bin) => {
      const t = (bin / series.length) * totalDuration;
      return findSegmentForTime(segments, t);
    })
    .filter((i, idx, arr) => i >= 0 && arr.indexOf(i) === idx);

  return segmentIdxs.sort((a, b) => a - b);
}

function findSegmentForTime(segments: Segment[], t: number): number {
  for (let i = 0; i < segments.length; i++) {
    if (t >= segments[i].startTime && t < segments[i].endTime) return i;
  }
  // After last segment's end → return last index
  if (segments.length > 0 && t >= segments[segments.length - 1].endTime) {
    return segments.length - 1;
  }
  return -1;
}

/**
 * Average the bins of a time-series that fall within each segment's
 * [startTime, endTime] window. Returns one number per segment.
 */
function perSegmentEnergyFromSeries(
  segments: Segment[],
  series: number[],
  totalDuration: number,
): number[] {
  if (totalDuration <= 0 || series.length === 0) return segments.map(() => 0);
  return segments.map((seg) => {
    const lo = Math.floor((seg.startTime / totalDuration) * series.length);
    const hi = Math.max(lo + 1, Math.ceil((seg.endTime / totalDuration) * series.length));
    let sum = 0;
    let n = 0;
    for (let i = lo; i < Math.min(series.length, hi); i++) {
      sum += series[i];
      n++;
    }
    return n > 0 ? sum / n : 0;
  });
}

function smooth(series: number[], window: number): number[] {
  if (window <= 1) return series.slice();
  const out: number[] = new Array(series.length);
  const half = Math.floor(window / 2);
  for (let i = 0; i < series.length; i++) {
    let sum = 0;
    let n = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(series.length - 1, i + half); j++) {
      sum += series[j];
      n++;
    }
    out[i] = sum / n;
  }
  return out;
}

function pickDrops(
  segments: Segment[],
  energies: number[],
  totalDuration: number,
  p75: number,
): number[] {
  // Candidates: segments within DROP_POSITION_RANGE whose energy is at or
  // above the 75th percentile (relaxes to 60th if no candidates qualify).
  const inRange = (i: number) => {
    const pos = segments[i].startTime / totalDuration;
    return pos >= DROP_POSITION_RANGE[0] && pos <= DROP_POSITION_RANGE[1];
  };

  let candidates = segments
    .map((_, i) => i)
    .filter((i) => inRange(i) && energies[i] >= p75);

  if (candidates.length === 0) {
    const p60 = percentile(energies, 0.6);
    candidates = segments.map((_, i) => i).filter((i) => inRange(i) && energies[i] >= p60);
  }
  if (candidates.length === 0) return [];

  // Sort descending by energy; greedily pick up to MAX_DROPS that are at
  // least MIN_DROP_GAP_SECONDS apart.
  candidates.sort((a, b) => energies[b] - energies[a]);
  const picked: number[] = [];
  for (const c of candidates) {
    const tc = segments[c].startTime;
    const tooClose = picked.some(
      (p) => Math.abs(segments[p].startTime - tc) < MIN_DROP_GAP_SECONDS,
    );
    if (!tooClose) picked.push(c);
    if (picked.length >= MAX_DROPS) break;
  }
  return picked.sort((a, b) => a - b);
}

// ───────────────────────── breakdown detection ─────────────────────────

/**
 * High-res breakdown detection: scan the smoothed series and mark sustained
 * low-energy spans (energy < median * 0.5, duration ≥ MIN_BREAKDOWN_SECONDS)
 * that lie outside the drop anchors.
 */
function markBreakdownsFromSeries(
  types: (SectionType | null)[],
  segments: Segment[],
  series: number[],
  totalDuration: number,
  dropIdxs: number[],
): void {
  if (series.length === 0) return;
  // Use a percentile-based threshold rather than a fixed `median * X` ratio.
  // p35 means roughly the bottom third of the smoothed signal qualifies as
  // "low-energy" — robust whether the track has tight or wide dynamic range.
  const lowThreshold = percentile(series, 0.35);
  const minBins = Math.max(
    1,
    Math.floor((MIN_BREAKDOWN_SECONDS / totalDuration) * series.length),
  );
  const dropBinsArr = dropIdxs.map((i) =>
    Math.floor(((segments[i].startTime + segments[i].endTime) / 2 / totalDuration) * series.length),
  );
  const dropBins = new Set(dropBinsArr);

  // Only scan BETWEEN the first and last drops. Pre-first-drop = intro
  // territory, post-last-drop = outro territory — don't override either.
  const scanFrom = Math.min(...dropBinsArr);
  const scanTo = Math.max(...dropBinsArr);
  if (scanFrom >= scanTo) return;

  let runStart = -1;
  for (let i = scanFrom; i <= scanTo; i++) {
    const inDrop = [...dropBins].some((d) => Math.abs(d - i) < 4);
    if (series[i] < lowThreshold && !inDrop) {
      if (runStart === -1) runStart = i;
    } else {
      if (runStart !== -1) {
        flushRunAsBreakdown(runStart, i - 1, minBins, series.length, totalDuration, segments, types);
        runStart = -1;
      }
    }
  }
  if (runStart !== -1) {
    flushRunAsBreakdown(runStart, scanTo, minBins, series.length, totalDuration, segments, types);
  }
}

function flushRunAsBreakdown(
  startBin: number,
  endBin: number,
  minBins: number,
  totalBins: number,
  totalDuration: number,
  segments: Segment[],
  types: (SectionType | null)[],
): void {
  if (endBin - startBin + 1 < minBins) return;
  const tStart = (startBin / totalBins) * totalDuration;
  const tEnd = ((endBin + 1) / totalBins) * totalDuration;
  for (let i = 0; i < segments.length; i++) {
    if (types[i] !== null) continue; // don't overwrite drops/builds/bookends
    const segMid = (segments[i].startTime + segments[i].endTime) / 2;
    if (segMid >= tStart && segMid <= tEnd) types[i] = 'breakdown';
  }
}

function markBreakdowns(
  types: (SectionType | null)[],
  segments: Segment[],
  energies: number[],
  dropIdxs: number[],
  median: number,
): void {
  const lowThreshold = median * 0.85;
  // Walk segments between consecutive drops and mark sustained low-energy
  // runs. We never scan before the first drop or after the last drop —
  // those are intro / outro territory and have their own pass.
  const ranges: Array<[number, number]> = [];
  for (let k = 0; k < dropIdxs.length - 1; k++) {
    ranges.push([dropIdxs[k] + 1, dropIdxs[k + 1] - 1]);
  }

  for (const [lo, hi] of ranges) {
    let runStart = -1;
    for (let i = lo; i <= hi; i++) {
      if (i < 0 || i >= segments.length) continue;
      if (types[i] !== null) {
        if (runStart !== -1) {
          maybeMarkBreakdown(types, segments, runStart, i - 1);
          runStart = -1;
        }
        continue;
      }
      if (energies[i] < lowThreshold) {
        if (runStart === -1) runStart = i;
      } else {
        if (runStart !== -1) {
          maybeMarkBreakdown(types, segments, runStart, i - 1);
          runStart = -1;
        }
      }
    }
    if (runStart !== -1) maybeMarkBreakdown(types, segments, runStart, hi);
  }
}

function maybeMarkBreakdown(
  types: (SectionType | null)[],
  segments: Segment[],
  start: number,
  end: number,
): void {
  if (start < 0 || end < start || end >= segments.length) return;
  const duration = segments[end].endTime - segments[start].startTime;
  if (duration < MIN_SECTION_SECONDS) return;
  for (let i = start; i <= end; i++) types[i] = 'breakdown';
}

// ───────────────────────── coalescing & post-processing ─────────────────────────

function coalesce(segments: Segment[], types: SectionType[]): Section[] {
  const out: Section[] = [];
  let cur: Section | null = null;
  segments.forEach((seg, i) => {
    const t = types[i];
    if (cur && cur.type === t) {
      cur.segments.push(seg);
      cur.endTime = seg.endTime;
    } else {
      if (cur) out.push(cur);
      cur = {
        type: t,
        label: '',
        startTime: seg.startTime,
        endTime: seg.endTime,
        segments: [seg],
        confidence: 0.7,
        avgEnergy: seg.energy || 0,
        timestamp: '',
      };
    }
  });
  if (cur) out.push(cur);
  return out;
}

function coalesceSections(sections: Section[]): Section[] {
  const out: Section[] = [];
  for (const s of sections) {
    const prev = out[out.length - 1];
    if (prev && prev.type === s.type) {
      prev.segments.push(...s.segments);
      prev.endTime = s.endTime;
    } else {
      out.push(s);
    }
  }
  return out;
}

function dropTiny(sections: Section[], totalDuration: number): Section[] {
  const minDur = Math.max(totalDuration * MIN_SECTION_FRACTION, MIN_SECTION_SECONDS);
  while (true) {
    if (sections.length <= 1) break;
    const tinyIdx = sections.findIndex(
      (s) =>
        s.endTime - s.startTime < minDur &&
        s.type !== 'mix-in' &&
        s.type !== 'mix-out' &&
        s.type !== 'main-drop', // never collapse a detected drop, even if short
    );
    if (tinyIdx === -1) break;

    const left = sections[tinyIdx - 1];
    const right = sections[tinyIdx + 1];
    const winner =
      !left ? right :
      !right ? left :
      avg(left.segments) >= avg(right.segments) ? left : right;
    winner.segments.push(...sections[tinyIdx].segments);
    winner.segments.sort((a, b) => a.startTime - b.startTime);
    winner.startTime = Math.min(winner.startTime, sections[tinyIdx].startTime);
    winner.endTime = Math.max(winner.endTime, sections[tinyIdx].endTime);
    sections.splice(tinyIdx, 1);
  }
  return coalesceSections(sections);
}

function enforceCaps(sections: Section[]): Section[] {
  const byType: Record<string, Section[]> = {};
  sections.forEach((s) => {
    s.avgEnergy = avg(s.segments);
    (byType[s.type] ||= []).push(s);
  });

  for (const [type, group] of Object.entries(byType)) {
    const cap = MAX_COUNTS[type as SectionType];
    if (group.length <= cap) continue;
    const ranked = [...group].sort((a, b) => b.avgEnergy - a.avgEnergy);
    const losers = ranked.slice(cap);
    losers.forEach((s) => {
      const lIdx = sections.indexOf(s);
      const neighbor = sections[lIdx - 1] || sections[lIdx + 1];
      s.type = pickFallbackType(s, neighbor);
    });
  }
  return sections;
}

function pickFallbackType(s: Section, neighbor: Section | undefined): SectionType {
  if (neighbor && neighbor.type !== 'mix-in' && neighbor.type !== 'mix-out') return neighbor.type;
  return s.avgEnergy > 0 ? 'verse' : 'breakdown';
}

function numberRepeats(sections: Section[], genreFamily?: string): void {
  // For electronic genres, both 'main-drop' (anchor peaks) and 'chorus'
  // (sustained high-energy plateaus between drops) are structurally "drops"
  // to a DJ — share the "Drop N" numbering pool while keeping the underlying
  // types distinct so the timeline doesn't collapse them into one section.
  const electronic = ELECTRONIC_FAMILIES.has(genreFamily ?? '');
  const labelGroup = (type: SectionType): string => {
    if (electronic && (type === 'main-drop' || type === 'chorus')) return 'drop-group';
    return type;
  };
  const baseFor = (type: SectionType): string => {
    if (electronic && (type === 'main-drop' || type === 'chorus')) return 'Drop';
    return displayBase(type);
  };

  const counts: Record<string, number> = {};
  sections.forEach((s) => {
    const g = labelGroup(s.type);
    counts[g] = (counts[g] || 0) + 1;
  });
  const seen: Record<string, number> = {};
  sections.forEach((s) => {
    const g = labelGroup(s.type);
    const total = counts[g];
    const baseName = baseFor(s.type);
    if (total > 1) {
      seen[g] = (seen[g] || 0) + 1;
      s.index = seen[g];
      s.label = `${baseName} ${s.index}`;
    } else {
      s.label = baseName;
    }
  });
}

function avg(segs: Segment[]): number {
  if (segs.length === 0) return 0;
  return segs.reduce((sum, s) => sum + (s.energy || 0), 0) / segs.length;
}

// ───────────────────────── finalize per-section aggregates ─────────────────────────

function finalizeSection(section: Section): void {
  section.avgEnergy = avg(section.segments);

  const keys = section.segments.map((s) => s.key).filter(Boolean) as string[];
  if (keys.length) {
    const counts: Record<string, number> = {};
    keys.forEach((k) => (counts[k] = (counts[k] || 0) + 1));
    section.dominantKey = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  }

  const allChords = section.segments.flatMap((s) => s.chordProgression || []);
  if (allChords.length) {
    const counts: Record<string, number> = {};
    allChords.forEach((c) => (counts[c] = (counts[c] || 0) + 1));
    section.chordProgression = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([c]) => c);
  }

  const instMap: Record<string, number> = {};
  section.segments.forEach((s) =>
    s.instruments?.forEach((inst) => {
      if (!instMap[inst.type] || inst.confidence > instMap[inst.type]) {
        instMap[inst.type] = inst.confidence;
      }
    }),
  );
  section.instruments = Object.entries(instMap)
    .map(([type, confidence]) => ({ type, confidence }))
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 8);

  section.timestamp = formatTimestamp(section.startTime, section.endTime);
}

function formatTimestamp(start: number, end: number): string {
  const fmt = (t: number) =>
    `${Math.floor(t / 60)}:${Math.floor(t % 60).toString().padStart(2, '0')}`;
  return `${fmt(start)} - ${fmt(end)}`;
}

// ───────────────────────── display helpers ─────────────────────────

function displayBase(type: SectionType): string {
  const names: Record<SectionType, string> = {
    'mix-in':    'Mix In',
    'intro':     'Intro',
    'breakdown': 'Breakdown',
    'main-drop': 'Drop',
    'bridge':    'Bridge',
    'outro':     'Outro',
    'mix-out':   'Mix Out',
    'verse':     'Verse',
    'chorus':    'Chorus',
    'build':     'Build',
  };
  return names[type];
}

export function getSectionDisplayName(type: SectionType): string {
  return displayBase(type);
}

export function getSectionLabel(section: Section): string {
  return section.label || displayBase(section.type);
}

export function getSectionColor(type: SectionType): string {
  const colors: Record<SectionType, string> = {
    'mix-in':    'rgba(128, 128, 128, 0.2)',
    'intro':     'rgba(0, 217, 255, 0.3)',
    'breakdown': 'rgba(0, 217, 255, 0.2)',
    'main-drop': 'rgba(236, 72, 153, 0.5)',
    'bridge':    'rgba(255, 193, 7, 0.3)',
    'outro':     'rgba(0, 217, 255, 0.2)',
    'mix-out':   'rgba(128, 128, 128, 0.2)',
    'verse':     'rgba(99, 102, 241, 0.3)',
    'chorus':    'rgba(236, 72, 153, 0.4)',
    'build':     'rgba(99, 102, 241, 0.4)',
  };
  return colors[type];
}
